import React, { useState, useEffect } from 'react';
import { Mail, Send, User, MessageSquare, CheckCircle, Briefcase, Home, Building2, Plane, Share2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { propertyData, brandAssets } from '../../data/mock';
import { toast } from 'sonner';
import { Facebook, Linkedin, Instagram } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Share message for social platforms
const SHARE_MESSAGE = "THE INFRASTRUCTURE EXISTS. THE NEXT OWNER DEFINES THE VISION - AN ESTABLISHED 20-ACRE CENTRAL FLORIDA LEGACY ESTATE PLATFORM.";

const SocialShareButtons = () => {
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  
  const handleFacebookShare = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(SHARE_MESSAGE)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };
  
  const handleLinkedInShare = () => {
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };
  
  const handleInstagramShare = () => {
    // Instagram doesn't have a direct web share API
    // Copy link to clipboard and prompt user to share on Instagram
    navigator.clipboard.writeText(`${SHARE_MESSAGE}\n\n${shareUrl}`).then(() => {
      toast.success('Link copied! Open Instagram to share this estate.');
    }).catch(() => {
      toast.info('Open Instagram and share this link: ' + shareUrl);
    });
  };
  
  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'William Lane Estate',
          text: SHARE_MESSAGE,
          url: shareUrl,
        });
      } catch (err) {
        // User cancelled or share failed
        if (err.name !== 'AbortError') {
          toast.error('Unable to share. Please try copying the link.');
        }
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(`${SHARE_MESSAGE}\n\n${shareUrl}`).then(() => {
        toast.success('Link copied to clipboard!');
      }).catch(() => {
        toast.info('Copy this link to share: ' + shareUrl);
      });
    }
  };
  
  return (
    <div className="flex items-center justify-center gap-4 mt-6" data-testid="social-share-container">
      <span className="text-white/50 text-sm mr-2">Share:</span>
      
      <button
        onClick={handleFacebookShare}
        className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/60 hover:bg-[#1877F2]/20 hover:border-[#1877F2]/50 hover:text-[#1877F2] transition-all duration-300"
        aria-label="Share on Facebook"
        data-testid="share-facebook-btn"
      >
        <Facebook className="w-5 h-5" />
      </button>
      
      <button
        onClick={handleLinkedInShare}
        className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/60 hover:bg-[#0A66C2]/20 hover:border-[#0A66C2]/50 hover:text-[#0A66C2] transition-all duration-300"
        aria-label="Share on LinkedIn"
        data-testid="share-linkedin-btn"
      >
        <Linkedin className="w-5 h-5" />
      </button>
      
      <button
        onClick={handleInstagramShare}
        className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/60 hover:bg-gradient-to-br hover:from-[#833AB4]/20 hover:via-[#FD1D1D]/20 hover:to-[#F77737]/20 hover:border-[#E1306C]/50 hover:text-[#E1306C] transition-all duration-300"
        aria-label="Share on Instagram"
        data-testid="share-instagram-btn"
      >
        <Instagram className="w-5 h-5" />
      </button>
      
      <button
        onClick={handleNativeShare}
        className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/60 hover:bg-amber-500/20 hover:border-amber-500/50 hover:text-amber-400 transition-all duration-300"
        aria-label="Share via device"
        data-testid="share-native-btn"
      >
        <Share2 className="w-5 h-5" />
      </button>
    </div>
  );
};

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    buyer_type: 'buyer',
    message: '',
    website: '', // Honeypot field - hidden, should always be empty
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  // UTM and tracking data
  const [trackingData, setTrackingData] = useState({
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
    referrer: '',
    source_page: '',
  });
  
  // Capture UTM parameters on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    setTrackingData({
      utm_source: urlParams.get('utm_source') || '',
      utm_medium: urlParams.get('utm_medium') || '',
      utm_campaign: urlParams.get('utm_campaign') || '',
      referrer: document.referrer || '',
      source_page: window.location.href,
    });
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrorMessage('');
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setErrorMessage('Please enter your name');
      return false;
    }
    if (formData.name.trim().length < 2) {
      setErrorMessage('Name must be at least 2 characters');
      return false;
    }
    if (!formData.email.trim()) {
      setErrorMessage('Please enter your email');
      return false;
    }
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setErrorMessage('Please enter a valid email address');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setErrorMessage('');

    try {
      const response = await fetch(`${API_URL}/api/inquiry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim().toLowerCase(),
          phone: formData.phone.trim() || null,
          buyer_type: formData.buyer_type,
          message: formData.message.trim() || null,
          website: formData.website, // Honeypot
          ...trackingData,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 429) {
          setErrorMessage('Too many requests. Please wait a moment before trying again.');
        } else if (response.status === 422) {
          // Validation error
          const detail = data.detail;
          if (Array.isArray(detail)) {
            setErrorMessage(detail[0]?.msg || 'Please check your information and try again.');
          } else {
            setErrorMessage(detail || 'Please check your information and try again.');
          }
        } else {
          setErrorMessage('Something went wrong. Please try again.');
        }
        return;
      }

      setIsSubmitted(true);
      toast.success('Your inquiry has been submitted successfully.');

    } catch (error) {
      console.error('Inquiry submission error:', error);
      setErrorMessage('Unable to submit inquiry. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setIsSubmitted(false);
    setFormData({
      name: '',
      email: '',
      phone: '',
      buyer_type: 'buyer',
      message: '',
      website: '',
    });
    setErrorMessage('');
  };

  const inquiryTypes = [
    { id: 'buyer', label: 'Private Buyer', icon: Home },
    { id: 'broker', label: 'Buyer Broker', icon: Briefcase },
    { id: 'investor', label: 'Investor / Advisor', icon: Building2 },
    { id: 'relocation', label: 'Business Owner / Relocation', icon: Plane },
  ];

  return (
    <section id="inquire" className="py-24 bg-gradient-to-b from-[#0d1117] to-[#161b22]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <img 
            src={brandAssets.wCrest} 
            alt="William Lane Estate" 
            className="w-20 h-20 mx-auto mb-6 object-contain"
          />
          <span className="inline-block px-4 py-1.5 bg-amber-500/10 border border-amber-500/30 rounded-full text-amber-400 text-sm font-medium tracking-wide mb-4">
            Direct Private Inquiry
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-white mb-6">
            Request Private Estate Information
          </h2>
          <p className="text-lg text-white/70 font-light">
            Connect directly with the estate for private viewings, detailed information packages, 
            or broker inquiries. All communications are confidential.
          </p>
          
          {/* Social Share Buttons */}
          <SocialShareButtons />
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Contact Form */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 to-amber-600/5 rounded-3xl blur-xl" />
            <div className="relative bg-[#1a1f2e]/80 backdrop-blur-xl border border-white/10 rounded-3xl p-8">
              {isSubmitted ? (
                <div className="text-center py-12" data-testid="inquiry-success">
                  <div className="w-20 h-20 mx-auto bg-green-500/20 rounded-full flex items-center justify-center mb-6">
                    <CheckCircle className="w-10 h-10 text-green-400" />
                  </div>
                  <h3 className="text-2xl font-semibold text-white mb-3">Inquiry Received</h3>
                  <p className="text-white/70 font-light mb-6">
                    Thank you. Your inquiry has been sent directly to the William Lane Estate family. We will respond personally.
                  </p>
                  <Button
                    onClick={resetForm}
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/10"
                  >
                    Submit Another Inquiry
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="inquiry-form">
                  {/* Error Message */}
                  {errorMessage && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-400 text-sm" data-testid="form-error">
                      {errorMessage}
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-white/80 text-sm mb-2">Full Name *</label>
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Enter your full name"
                        className="pl-12 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-amber-500/50 focus:ring-amber-500/20"
                        data-testid="inquiry-name-input"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-white/80 text-sm mb-2">Email Address *</label>
                      <div className="relative">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                        <Input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="your@email.com"
                          className="pl-12 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-amber-500/50 focus:ring-amber-500/20"
                          data-testid="inquiry-email-input"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-white/80 text-sm mb-2">Phone Number</label>
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="(555) 123-4567"
                        className="bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-amber-500/50 focus:ring-amber-500/20"
                        data-testid="inquiry-phone-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-white/80 text-sm mb-3">I am a...</label>
                    <div className="grid grid-cols-2 gap-3">
                      {inquiryTypes.map((type) => (
                        <button
                          key={type.id}
                          type="button"
                          onClick={() => setFormData((prev) => ({ ...prev, buyer_type: type.id }))}
                          className={`p-4 rounded-xl border flex flex-col items-center space-y-2 transition-all duration-300 ${
                            formData.buyer_type === type.id
                              ? 'bg-amber-500/20 border-amber-500/50 text-amber-400'
                              : 'bg-white/5 border-white/10 text-white/60 hover:border-white/20'
                          }`}
                          data-testid={`buyer-type-${type.id}`}
                        >
                          <type.icon className="w-5 h-5" />
                          <span className="text-xs font-medium text-center">{type.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-white/80 text-sm mb-2">Message (Optional)</label>
                    <div className="relative">
                      <MessageSquare className="absolute left-4 top-4 w-5 h-5 text-white/40" />
                      <Textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us about your interest, timeline, or specific questions..."
                        rows={4}
                        className="pl-12 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-amber-500/50 focus:ring-amber-500/20 resize-none"
                        data-testid="inquiry-message-input"
                      />
                    </div>
                  </div>

                  {/* Honeypot field - hidden from real users */}
                  <div className="absolute opacity-0 pointer-events-none" aria-hidden="true" style={{ position: 'absolute', left: '-9999px' }}>
                    <label htmlFor="website">Website</label>
                    <input
                      type="text"
                      name="website"
                      id="website"
                      value={formData.website}
                      onChange={handleChange}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white py-6 text-lg shadow-xl shadow-amber-500/30"
                    data-testid="inquiry-submit-btn"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending Inquiry...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center">
                        <Send className="w-5 h-5 mr-3" />
                        SUBMIT PRIVATE INQUIRY
                      </span>
                    )}
                  </Button>

                  {/* Privacy Copy */}
                  <p className="text-white/40 text-xs text-center">
                    Your information will be used only to respond to your inquiry regarding this property and will not be sold.
                  </p>
                </form>
              )}
            </div>
          </div>

          {/* Info Panel */}
          <div className="space-y-6">
            {/* Who This Estate Is For */}
            <div className="bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 rounded-2xl p-8">
              <h3 className="text-xl font-semibold text-white mb-6">Who This Estate Is For</h3>
              <ul className="space-y-4">
                {propertyData.idealBuyers.map((buyer, index) => (
                  <li key={index} className="flex items-start">
                    <div className="w-2 h-2 rounded-full bg-amber-400 mt-2 mr-3 flex-shrink-0" />
                    <div>
                      <span className="text-white font-medium">{buyer.profile}</span>
                      <p className="text-white/60 text-sm font-light">{buyer.benefit}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Estate Quick Info */}
            <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border border-amber-500/30 rounded-2xl p-8">
              <div className="flex items-center justify-between mb-4">
                <span className="text-white/70">Current Offering</span>
                <span className="text-3xl font-bold text-amber-400">{propertyData.priceShort}</span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between text-white/60">
                  <span>Price per Acre</span>
                  <span className="text-white">{propertyData.pricePerAcreFormatted}</span>
                </div>
                <div className="flex items-center justify-between text-white/60">
                  <span>Total Acreage</span>
                  <span className="text-white">{propertyData.keyStats.acres} acres</span>
                </div>
              </div>
              <div className="mt-6 pt-6 border-t border-amber-500/20">
                <p className="text-white/60 text-sm">
                  {propertyData.marketComparison.savings}
                </p>
                <p className="text-white/40 text-xs mt-2 italic">
                  {propertyData.marketComparison.disclaimer}
                </p>
              </div>
            </div>

            {/* Direct Contact Email */}
            <div className="bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 rounded-2xl p-6 text-center">
              <p className="text-white/60 text-sm mb-2">Direct Estate Email</p>
              <a 
                href={`mailto:${propertyData.estateContact.email}?subject=Inquiry: ${propertyData.brandName}`}
                className="text-amber-400 hover:text-amber-300 transition-colors font-medium"
              >
                {propertyData.estateContact.email}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
